import { AuthErrorCodes } from 'firebase/auth';

import { alert, errorAlert } from '@helpers/Alert';
import { logError } from '@helpers/Loggers';

type AuthErrorAlert = {
  message: string;
  title: string;
};

export const AUTH_ERROR_ALERTS = {
  accountExists: {
    title: 'Account Exists',
    message:
      'An account with that email address already exists with another provider. Please try a different provider or use another account.',
  },
  emailExists: {
    title: 'Email Already In Use',
    message: 'That email address is already in use. Please use a different one.',
  },
  invalidCredential: {
    title: 'Invalid Credential',
    message: 'The provided credential is invalid. Please try again.',
  },
  invalidEmail: {
    title: 'Invalid Email',
    message: 'That email address is invalid. Please enter a valid email address.',
  },
  invalidPassword: {
    title: 'Invalid Password',
    message: "The password you entered doesn't match our records. Please try again.",
  },
  operationNotAllowed: {
    title: 'Operation Not Allowed',
    message: 'Please try a different sign-in method.',
  },
  recentLoginRequired: {
    title: 'Sign In Again',
    message: 'For your security, please sign out and sign back in before deleting your account.',
  },
  userDeleted: {
    title: 'User Not Found',
    message: "We couldn't find an account with that email address. Please try again.",
  },
  userDisabled: {
    title: 'User Disabled',
    message: 'That user has been disabled. Please sign-in with a different user.',
  },
  weakPassword: {
    title: 'Weak Password',
    message: 'That password is too short. Passwords must contain at least 6 characters.',
  },
} as const satisfies Record<string, AuthErrorAlert>;

export function handleAuthError(error: unknown) {
  switch (getAuthErrorCode(error)) {
    case AuthErrorCodes.EMAIL_EXISTS:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.emailExists);
      break;
    case AuthErrorCodes.WEAK_PASSWORD:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.weakPassword);
      break;
    case AuthErrorCodes.INVALID_EMAIL:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.invalidEmail);
      break;
    case AuthErrorCodes.INVALID_PASSWORD:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.invalidPassword);
      break;
    case AuthErrorCodes.USER_DELETED:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.userDeleted);
      break;
    case AuthErrorCodes.OPERATION_NOT_ALLOWED:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.operationNotAllowed);
      break;
    case AuthErrorCodes.USER_DISABLED:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.userDisabled);
      break;
    case AuthErrorCodes.NEED_CONFIRMATION:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.accountExists);
      break;
    case AuthErrorCodes.INVALID_IDP_RESPONSE:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.invalidCredential);
      break;
    case AuthErrorCodes.CREDENTIAL_TOO_OLD_LOGIN_AGAIN:
      showAuthErrorAlert(AUTH_ERROR_ALERTS.recentLoginRequired);
      break;
    default:
      errorAlert(error);
      logError(error);
  }
}

function getAuthErrorCode(error: unknown) {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return undefined;
  }

  const code = error.code;
  return typeof code === 'string' ? code : undefined;
}

function showAuthErrorAlert({ title, message }: AuthErrorAlert) {
  alert(title, message);
}
